import YAML from 'yaml'
import type { RenderData, RenderOptions } from '../assetTypes'
import buildDatasetDataApiScript from '../datasets/dataApiScript'
import { collectAssetReferencesFromRenderData } from '../shared/assetReferences'

const insightsServiceUrl = 'https://api.shared-platform.eidu.com/test-insights'

type ChartPayload = {
  dataset?: unknown
  spec?: unknown
  params?: unknown
  title?: unknown
}

const resolveFallbackName = (options?: RenderOptions): string => {
  const fields = options?.data?.entity?.fields
  if (!fields) return ''
  const nameField = fields.find((field) => field.name.trim().toLowerCase() === 'name')
  return nameField?.value.trim() ?? ''
}

const normalizeChartTitle = (rawValue: unknown): string => {
  if (rawValue === null || rawValue === undefined) return ''
  const value = typeof rawValue === 'string' ? rawValue : String(rawValue)
  return value.trim()
}

const buildChartsRenderer = (content: string, options?: RenderOptions) => {
  let parseError = ''
  let datasetRef = ''
  let spec: unknown = null
  let params: Record<string, string | string[]> = {}
  let parsedTitle = ''
  let hasExplicitTitle = false

  try {
    const parsed = YAML.parse(content || '') as ChartPayload
    if (!parsed || typeof parsed !== 'object') {
      parseError = 'Chart YAML must be an object.'
    } else {
      datasetRef = typeof parsed.dataset === 'string' ? parsed.dataset.trim() : ''
      spec = parsed.spec ?? null
      hasExplicitTitle = Object.prototype.hasOwnProperty.call(parsed, 'title')
      if (hasExplicitTitle) {
        parsedTitle = normalizeChartTitle(parsed.title)
      }
      if (!spec) {
        parseError = 'Chart YAML must include a "spec" block.'
      } else if (typeof spec !== 'object') {
        parseError = 'Chart "spec" must be an object.'
      }
      const rawParams = parsed.params
      if (Array.isArray(rawParams)) {
        rawParams.forEach((entry) => {
          if (!entry || typeof entry !== 'object') return
          const record = entry as Record<string, unknown>
          const name = typeof record.name === 'string' ? record.name.trim() : ''
          if (!name) return
          const value = record.value
          if (value === null || value === undefined) return
          if (Array.isArray(value)) {
            params[name] = value.filter((item) => item !== null && item !== undefined).map((item) => String(item))
          } else {
            params[name] = String(value)
          }
        })
      } else if (rawParams && typeof rawParams === 'object') {
        Object.entries(rawParams as Record<string, unknown>).forEach(([key, value]) => {
          const name = key.trim()
          if (!name) return
          if (value === null || value === undefined) return
          if (Array.isArray(value)) {
            params[name] = value.filter((item) => item !== null && item !== undefined).map((item) => String(item))
          } else {
            params[name] = String(value)
          }
        })
      }
    }
  } catch (error) {
    parseError = `Failed to parse YAML: ${error instanceof Error ? error.message : String(error)}`
  }

  const serializedDatasetRef = JSON.stringify(datasetRef)
  const serializedSpec = JSON.stringify(spec ?? null)
  const serializedParseError = JSON.stringify(parseError)
  const serializedParams = JSON.stringify(params)
  const fallbackName = resolveFallbackName(options)
  const resolvedBaseTitle = hasExplicitTitle ? parsedTitle : fallbackName
  const serializedBaseTitle = JSON.stringify(resolvedBaseTitle)
  const datasetDataApiScript = buildDatasetDataApiScript(insightsServiceUrl)

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background-color: #ffffff;
        color: #111827;
        width: 100vw;
        height: 100vh;
        overflow: hidden;
      }
      #root {
        position: relative;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        padding: 12px;
      }
      .chart-title {
        margin: 0 0 8px;
        color: #000000;
        font-family: sans-serif;
        font-size: 13px;
        font-style: normal;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: normal;
      }
      #chart-content {
        position: relative;
        flex: 1 1 auto;
        min-height: 0;
      }
      #chart-root {
        position: absolute;
        inset: 5px;
      }
      .chart-no-data {
        font-size: 13px;
        color: #475569;
      }
      .chart-status {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        text-align: center;
        font-size: 13px;
        color: #64748b;
        background: #ffffff;
      }
      .chart-status.error {
        color: #b91c1c;
      }
    </style>
    <script src="https://cdn.jsdelivr.net/npm/yaml@2.6.1/dist/yaml.min.js"></script>
    <script src="https://cdn.jsdelivr.net/npm/vega@5"></script>
    <script src="https://cdn.jsdelivr.net/npm/vega-lite@5"></script>
    <script src="https://cdn.jsdelivr.net/npm/vega-embed@6"></script>
  </head>
  <body>
    <div id="root">
      <div id="chart-title" class="chart-title" style="display:none"></div>
      <div id="chart-content">
        <div id="chart-root"></div>
        <div id="no-data" class="chart-no-data" style="display:none">No data.</div>
        <div id="status" class="chart-status" style="display: none;"></div>
      </div>
    </div>
    <script>
      const spec = ${serializedSpec};
      const datasetRef = ${serializedDatasetRef};
      const chartParams = ${serializedParams};
      const baseTitle = ${serializedBaseTitle};
      const parseError = ${serializedParseError};
      const renderData = ${JSON.stringify(options?.data ?? null)};
      const auth = renderData && renderData.auth ? renderData.auth : null;
      const root = document.getElementById('root');
      const chartRoot = document.getElementById('chart-root');
      const titleEl = document.getElementById('chart-title');
      const noDataEl = document.getElementById('no-data');
      const statusEl = document.getElementById('status');
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      let isRunning = false;
      let pendingRun = false;
      let lastParamKey = '';
      const trellis = window.trellis;

      const logMessage = (message) => {
        trellis.log(message);
      };

      const setStatus = (message, isError) => {
        const ui = trellis.ui && typeof trellis.ui === 'object' ? trellis.ui : null;
        if (!message) {
          if (ui && typeof ui.clear === 'function') {
            ui.clear();
          }
          if (!statusEl) return;
          statusEl.style.display = 'none';
          statusEl.textContent = '';
          statusEl.classList.remove('error');
          return;
        }
        const normalizedMessage = String(message);
        if (ui) {
          if (isError && typeof ui.showError === 'function') {
            ui.showError(normalizedMessage);
            return;
          }
          if (!isError && typeof ui.showLoading === 'function') {
            ui.showLoading(normalizedMessage);
            return;
          }
        }
        if (!statusEl) return;
        statusEl.style.display = 'flex';
        statusEl.textContent = normalizedMessage;
        statusEl.classList.toggle('error', Boolean(isError));
      };

      const showSpinner = () => {
        const ui = trellis.ui && typeof trellis.ui === 'object' ? trellis.ui : null;
        if (ui && typeof ui.showLoading === 'function') {
          ui.showLoading('');
          return;
        }
        setStatus('Loading...', false);
      };

${datasetDataApiScript}
      const datasetDataApi = createDatasetDataApi({ auth, logMessage });

      const normalizeProvidedValues = (provided) => datasetDataApi.normalizeProvidedValues(provided);

      const getRawProvidedValues = () => {
        const values = trellis.params.get();
        return values && typeof values === 'object' ? values : {};
      };

      const getProvidedValues = () => {
        return normalizeProvidedValues(getRawProvidedValues());
      };

      const mergeParams = (baseValues, overrideValues) => ({
        ...normalizeProvidedValues(baseValues),
        ...normalizeProvidedValues(overrideValues),
      });

      const resolveTitleOverride = (provided) => {
        if (!provided || typeof provided !== 'object') return { hasOverride: false, value: '' };
        const titleKey = Object.keys(provided).find((key) => key.trim().toLowerCase() === 'title');
        if (!titleKey) return { hasOverride: false, value: '' };
        const rawValue = provided[titleKey];
        if (Array.isArray(rawValue)) {
          const first = rawValue.find((entry) => entry !== null && entry !== undefined);
          return { hasOverride: true, value: first === undefined ? '' : String(first).trim() };
        }
        if (rawValue === null || rawValue === undefined) return { hasOverride: true, value: '' };
        return { hasOverride: true, value: String(rawValue).trim() };
      };

      const renderTitle = (value) => {
        if (!titleEl) return;
        if (value) {
          titleEl.textContent = value;
          titleEl.style.display = 'block';
          return;
        }
        titleEl.textContent = '';
        titleEl.style.display = 'none';
      };

      const applyTitle = (rawProvidedValues) => {
        const override = resolveTitleOverride(rawProvidedValues);
        renderTitle(override.hasOverride ? override.value : baseTitle);
      };

      const hideNoDataState = () => {
        if (chartRoot) {
          chartRoot.style.display = 'block';
        }
        if (noDataEl) {
          noDataEl.style.display = 'none';
        }
      };

      const showNoDataState = () => {
        if (chartRoot) {
          chartRoot.innerHTML = '';
          chartRoot.style.display = 'none';
        }
        if (noDataEl) {
          noDataEl.style.display = 'block';
        }
      };

      const needsContainerLayout = (chartSpec) =>
        Boolean(
          chartSpec &&
            typeof chartSpec === 'object' &&
            (chartSpec.width === 'container' || chartSpec.height === 'container')
        );

      const waitForRenderableContainer = async () => {
        if (!chartRoot) return;
        const isReady = () => chartRoot.clientWidth > 0 && chartRoot.clientHeight > 0;
        if (isReady()) return;
        await new Promise((resolve) => {
          let frames = 0;
          const poll = () => {
            if (!chartRoot || isReady() || frames >= 120) {
              resolve(undefined);
              return;
            }
            frames += 1;
            requestAnimationFrame(poll);
          };
          requestAnimationFrame(poll);
        });
      };

      const getPreloadedDatasetPayload = (datasetId) => {
        if (!trellis.assets || typeof trellis.assets.get !== 'function') return null;
        try {
          const handle = trellis.assets.get('asset://' + datasetId);
          if (!handle || typeof handle !== 'object') return null;
          if (
            Object.prototype.hasOwnProperty.call(handle, 'datasetPayload') &&
            handle.datasetPayload &&
            typeof handle.datasetPayload === 'object'
          ) {
            return handle.datasetPayload;
          }
          if (typeof handle.text === 'string' && handle.text.trim().length > 0) {
            return handle.text;
          }
        } catch {
          return null;
        }
        return null;
      };

      const resolveDatasetPayload = async (datasetId) => {
        const preloadedPayload = getPreloadedDatasetPayload(datasetId);
        if (preloadedPayload !== null) {
          return preloadedPayload;
        }
        if (!auth || !auth.accessToken || !auth.organizationId || !auth.entityApiUrl) {
          throw new Error('Missing auth context.');
        }
        return datasetDataApi.resolveDatasetPayload(datasetId);
      };

      const renderChart = async (chartSpec) => {
        if (!root || !chartRoot) return;
        if (!window.vegaEmbed) throw new Error('Vega-Lite renderer is not available.');
        if (needsContainerLayout(chartSpec)) {
          await waitForRenderableContainer();
        }
        chartRoot.innerHTML = '';
        await window.vegaEmbed(chartRoot, chartSpec, { actions: false });
      };

      const run = async () => {
        const rawProvidedValues = getRawProvidedValues();
        applyTitle(rawProvidedValues);

        if (parseError) {
          hideNoDataState();
          setStatus(parseError, true);
          logMessage('Chart YAML parse error: ' + parseError);
          return;
        }
        if (!spec || typeof spec !== 'object') {
          hideNoDataState();
          setStatus('Chart spec is missing.', true);
          return;
        }

        const providedValues = mergeParams(chartParams, rawProvidedValues);
        const paramKey = JSON.stringify(providedValues);
        if (paramKey === lastParamKey && !pendingRun) return;
        lastParamKey = paramKey;

        const hasSpecData = Object.prototype.hasOwnProperty.call(spec, 'data');
        const normalizedDataset = typeof datasetRef === 'string' ? datasetRef.trim() : '';

        if (hasSpecData) {
          if (normalizedDataset) {
            logMessage('Chart spec includes data; dataset reference ignored.');
          }
          hideNoDataState();
          await renderChart(spec);
          return;
        }

        if (!normalizedDataset) {
          hideNoDataState();
          setStatus('Chart YAML must include a dataset reference or spec.data.', true);
          return;
        }
        if (!normalizedDataset.startsWith('asset://')) {
          hideNoDataState();
          setStatus('Chart dataset must be asset://<uuid>.', true);
          return;
        }
        const datasetId = normalizedDataset.slice('asset://'.length);
        if (!uuidPattern.test(datasetId)) {
          hideNoDataState();
          setStatus('Chart dataset reference is not a valid UUID.', true);
          return;
        }

        showSpinner();
        const datasetPayload = await resolveDatasetPayload(datasetId);
        const data = await datasetDataApi.loadDatasetRows({ payload: datasetPayload, providedValues });
        const rows = Array.isArray(data && data.rows) ? data.rows : [];
        if (rows.length === 0) {
          setStatus('', false);
          showNoDataState();
          return;
        }
        hideNoDataState();
        const specWithData = { ...(spec || {}), data: { values: data.rows || [] } };
        setStatus('', false);
        await renderChart(specWithData);
      };

      const scheduleRun = () => {
        if (isRunning) {
          pendingRun = true;
          return;
        }
        isRunning = true;
        setTimeout(() => {
          void run().catch((error) => {
            const message = error && error.message ? error.message : String(error);
            setStatus('Failed to render chart: ' + message, true);
            logMessage('Chart renderer error: ' + message);
          }).finally(() => {
            isRunning = false;
            if (pendingRun) {
              pendingRun = false;
              scheduleRun();
            }
          });
        }, 0);
      };

      trellis.runtime.registerParamsListener((event) => {
        const changedKeys = Array.isArray(event && event.changedKeys) ? event.changedKeys : [];
        if (!event || event.isInitial === true || changedKeys.length > 0) {
          scheduleRun();
        }
      });
    </script>
  </body>
</html>`
}

export default buildChartsRenderer

export const collectAssetReferences = (content: string, renderData?: RenderData) =>
  collectAssetReferencesFromRenderData(content, renderData)
